import moment from "moment";
import Alert from "@mui/material/Alert";
import ErrorIcon from "@mui/icons-material/Error";

import type { FC } from "react";
import type { AlertColor } from "@mui/material/Alert";

const sxStyles = {
  // Full-bleed: no radius and no margin, so the banner spans the card the way
  // the purple header above it does.
  alert: {
    borderRadius: 0,
    padding: "0.375rem 1rem",
    fontSize: "0.8125rem",
    fontWeight: 700,
    alignItems: "center",
    "& .MuiAlert-icon": { marginRight: "0.5rem", padding: 0 },
    "& .MuiAlert-message": { padding: 0 },
  },
};

interface DischargeBucket {
  label: string;
  severity: AlertColor;
}

// "Discharging soon" only — today or tomorrow (ENG-30846). A date further out
// (or already past) isn't news worth a banner, so it collapses, as does a
// patient with no EDD in PE at all.
//
// The two buckets are the only place severity colour survives on the card: the
// risk chip reads by word now (ENG-30844), so red/amber here announce an
// imminent discharge rather than competing with it.
const dischargeBucket = (edd: string | null): DischargeBucket | null => {
  if (!edd) return null;
  // Explicit format so the date lands on local midnight rather than being read
  // as UTC (which shifts the day west of GMT), and strict so only the exact
  // shape the API promises is accepted. Loose parsing would quietly coerce a
  // partial value — "2026-08" becomes the 1st of the month — and announce a
  // date the patient doesn't have. (Overflowed dates like "2026-02-30" moment
  // rejects either way.)
  const eddDate = moment(edd, "YYYY-MM-DD", true);
  if (!eddDate.isValid()) return null;
  const daysOut = eddDate
    .clone()
    .startOf("day")
    .diff(moment().startOf("day"), "days");
  if (daysOut === 0) return { label: "Discharging today", severity: "error" };
  if (daysOut === 1)
    return { label: "Discharging tomorrow", severity: "warning" };
  return null;
};

interface MalnutritionDischargeBannerProps {
  edd: string | null;
}

const MalnutritionDischargeBanner: FC<MalnutritionDischargeBannerProps> = ({
  edd,
}) => {
  const bucket = dischargeBucket(edd);
  if (!bucket) return null;

  return (
    <Alert
      severity={bucket.severity}
      // MUI defaults to the outlined glyphs (and a triangle for warning); the
      // design uses the one filled Error mark for both buckets.
      icon={<ErrorIcon fontSize="inherit" />}
      sx={sxStyles.alert}
    >
      {bucket.label}
    </Alert>
  );
};

export default MalnutritionDischargeBanner;
